import React, { useMemo, useRef } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid } from 'recharts';
import useIsMobile from '../hooks/useIsMobile';
import { getItemTypeColor } from '../utils/chartUtils';
import LeadTimeChartTypeSelector from './LeadTimeChartTypeSelector';
import Filters from './Filters';
import DateRangeFilter from './DateRangeFilter';
import MetricsFilters from './MetricsFilters';
import { toPng } from 'html-to-image';
import { saveAs } from 'file-saver';
import { sanitizeText } from '../utils/sanitization';

function LeadTimeAnalysis({ 
  data, 
  allTypes, 
  visibleTypes, 
  visiblePercentiles,
  visibleMetrics,
  setVisibleTypes, 
  togglePercentile,
  toggleMetric,
  leadTimeChartType,
  setLeadTimeChartType,
  plausible
}) {
  const isMobile = useIsMobile();
  const chartAreaRef = useRef(null);

  // Estado para ordenação da tabela
  const [sortConfig, setSortConfig] = React.useState({ key: 'closedDate', direction: 'desc' });
  
  // Estados para controlar colapso/expansão das seções
  const [isTableExpanded, setIsTableExpanded] = React.useState(true);
  const [isTimelineExpanded, setIsTimelineExpanded] = React.useState(true);
  const [isDistributionExpanded, setIsDistributionExpanded] = React.useState(true);

  // Determina datas mín/max para o filtro de período
  const closedDates = useMemo(() => data.map(item => new Date(item['Closed Date'])).sort((a, b) => a - b), [data]);
  const minDate = closedDates.length ? closedDates[0].toISOString().slice(0, 10) : '';
  const maxDate = closedDates.length ? closedDates[closedDates.length - 1].toISOString().slice(0, 10) : '';
  const [startDate, setStartDate] = React.useState(minDate);
  const [endDate, setEndDate] = React.useState(maxDate);

  // Atualiza datas do filtro sempre que minDate/maxDate mudarem (ex: novo arquivo)
  React.useEffect(() => {
    setStartDate(minDate);
    setEndDate(maxDate);
  }, [minDate, maxDate]);

  // Função para ordenar tabela
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Função para formatar data
  const formatDateBR = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleDateString('pt-BR');
  };

  // Função de exportação de imagem
  const handleExport = () => {
    if (!chartAreaRef.current) return;
    
    // Track the export action
    if (plausible) {
      plausible.trackExport('png', 'leadtime', 'leadtime-analysis');
      plausible.trackSection('export-png');
    }
    
    toPng(chartAreaRef.current, { backgroundColor: '#fff' })
      .then((dataUrl) => {
        saveAs(dataUrl, 'leadtime-analysis.png');
      })
      .catch(() => {
        alert('Erro ao exportar imagem');
      });
  };

  // Dados filtrados por tipos visíveis e período selecionado
  const filteredData = useMemo(() => {
    return data.filter(item => {
      const closedDate = new Date(item['Closed Date']);
      const type = item['Tipo de Item']?.trim() || 'Item';
      const isInPeriod = closedDate >= new Date(startDate) && closedDate <= new Date(endDate);
      const isVisibleType = visibleTypes.includes(type);
      return isInPeriod && isVisibleType;
    });
  }, [data, visibleTypes, startDate, endDate]);

  // Dados ordenados para a tabela
  const sortedTableData = useMemo(() => {
    const sorted = [...filteredData].sort((a, b) => {
      if (sortConfig.key === 'id') {
        const aVal = a['ID'] || '';
        const bVal = b['ID'] || '';
        return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      if (sortConfig.key === 'type') {
        const aVal = a['Tipo de Item'] || '';
        const bVal = b['Tipo de Item'] || '';
        return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      if (sortConfig.key === 'committedDate') {
        const aVal = new Date(a['Committed Date'] || 0);
        const bVal = new Date(b['Committed Date'] || 0);
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      if (sortConfig.key === 'closedDate') {
        const aVal = new Date(a['Closed Date'] || 0);
        const bVal = new Date(b['Closed Date'] || 0);
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      if (sortConfig.key === 'leadTime') {
        const aVal = a.leadTime || 0;
        const bVal = b.leadTime || 0;
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return 0;
    });
    return sorted;
  }, [filteredData, sortConfig]);

  // 1. Análise de tendência temporal - Estatísticas consolidadas por mês
  const monthlyTrend = useMemo(() => {
    const grouped = {};
    
    filteredData.forEach(item => {
      const closedDate = new Date(item['Closed Date']);
      const year = closedDate.getUTCFullYear();
      const month = closedDate.getUTCMonth();
      const monthKey = `${year}-${(month + 1).toString().padStart(2, '0')}`;
      
      if (!grouped[monthKey]) {
        grouped[monthKey] = { 
          month: monthKey,
          items: []
        };
      }
      
      grouped[monthKey].items.push(item.leadTime);
    });

    // Converte para formato que o Recharts espera com todas as estatísticas
    return Object.values(grouped)
      .map(group => {
        const items = group.items.sort((a, b) => a - b);
        const total = items.reduce((sum, val) => sum + val, 0);
        
        return {
          month: group.month,
          avg: Math.round(total / items.length),
          median: calculateMedian(items),
          p50: calculatePercentile(items, 50),
          p85: calculatePercentile(items, 85),
          p95: calculatePercentile(items, 95),
          count: items.length
        };
      })
      .filter(monthData => monthData.count > 0)
      .sort((a, b) => a.month.localeCompare(b.month));
  }, [filteredData]);

  // Função para definir cores das faixas (evitando azul que pode confundir com User Story)
  const getDistributionColor = (index) => {
    const colors = [
      '#10b981', // Verde
      '#f59e0b', // Amarelo
      '#ef4444', // Vermelho
      '#8b5cf6', // Roxo
      '#6b7280'  // Cinza
    ];
    return colors[index] || '#6b7280';
  };

  // 2. Distribuição por faixas de Lead Time
  const distributionRanges = useMemo(() => {
    const ranges = [
      { label: '1-5 dias', min: 1, max: 5 },
      { label: '6-10 dias', min: 6, max: 10 },
      { label: '11-20 dias', min: 11, max: 20 },
      { label: '21-30 dias', min: 21, max: 30 },
      { label: '31+ dias', min: 31, max: Infinity }
    ];

    return ranges.map((range, index) => {
      const items = filteredData.filter(item => 
        item.leadTime >= range.min && item.leadTime <= range.max
      );
      
      return {
        range: range.label,
        count: items.length,
        percentage: Math.round((items.length / filteredData.length) * 100),
        items: items.map(item => ({
          id: item['ID'] || '-',
          type: item['Tipo de Item']?.trim() || 'Item',
          leadTime: item.leadTime
        })),
        color: getDistributionColor(index)
      };
    });
  }, [filteredData]);

  // Função auxiliar para calcular mediana
  function calculateMedian(values) {
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 !== 0 
      ? sorted[mid] 
      : Math.round((sorted[mid - 1] + sorted[mid]) / 2);
  }

  // Função auxiliar para calcular percentis
  function calculatePercentile(values, percentile) {
    const sorted = [...values].sort((a, b) => a - b);
    const index = (percentile / 100) * (sorted.length - 1);
    const lower = Math.floor(index);
    const upper = Math.ceil(index);
    
    if (lower === upper) {
      return sorted[lower];
    }
    
    const weight = index - lower;
    return Math.round(sorted[lower] * (1 - weight) + sorted[upper] * weight);
  }

  // Função de debug para validar cálculos (pode ser removida depois)
  const debugStatistics = (values, label = "Dataset") => {
    if (values.length === 0) return;
    
    // Cálculos estatísticos estão validados via testes automatizados
    // Logs removidos para performance em produção
  };

  // Formatar mês para exibição
  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 
                   'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return `${months[parseInt(month) - 1]}/${year}`;
  };

  return (
    <div className="w-full max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-1 sm:p-6 mt-8 mb-16 px-0 sm:px-2 flex flex-col items-stretch relative">
      <LeadTimeChartTypeSelector
        selectedType={leadTimeChartType}
        onTypeChange={setLeadTimeChartType}
        onExport={handleExport}
      />
      
      <Filters
        allTypes={allTypes}
        visibleTypes={visibleTypes}
        setVisibleTypes={setVisibleTypes}
        visiblePercentiles={{}} // Análise não usa percentis
        togglePercentile={() => {}}
      />

      <DateRangeFilter
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        minDate={minDate}
        maxDate={maxDate}
        className="mb-2"
      />

      <div ref={chartAreaRef} className="space-y-8 p-4">
        {/* Tabela de Dados Importados */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div 
            className="flex items-center justify-between cursor-pointer hover:bg-gray-100 -m-4 p-4 rounded-lg transition-colors"
            onClick={() => setIsTableExpanded(!isTableExpanded)}
          >
            <h3 className="text-lg font-semibold text-gray-800">
              Dados Importados ({sortedTableData.length} itens)
              {!isTableExpanded && <span className="text-sm font-normal text-gray-500 ml-2">(clique para expandir)</span>}
            </h3>
            <button 
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              type="button"
            >
              <span>{isTableExpanded ? 'Colapsar' : 'Expandir'}</span>
              <span className={`transform transition-transform duration-300 ${isTableExpanded ? 'rotate-180' : ''}`}>
                ▼
              </span>
            </button>
          </div>
          
          {isTableExpanded && (
            <div className="mt-4 overflow-x-auto border rounded-lg bg-white shadow-lg max-h-96 overflow-y-auto animate-in slide-in-from-top-2 duration-300">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th 
                      className={`${isMobile ? 'px-2 py-2' : 'px-4 py-3'} text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100`}
                      onClick={() => handleSort('id')}
                    >
                      <div className="flex items-center gap-1">
                        ID
                        {sortConfig.key === 'id' && (
                          <span className="text-blue-600">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th 
                      className={`${isMobile ? 'px-2 py-2' : 'px-4 py-3'} text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100`}
                      onClick={() => handleSort('type')}
                    >
                      <div className="flex items-center gap-1">
                        Tipo de Item
                        {sortConfig.key === 'type' && (
                          <span className="text-blue-600">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th 
                      className={`${isMobile ? 'px-2 py-2' : 'px-4 py-3'} text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100`}
                      onClick={() => handleSort('committedDate')}
                    >
                      <div className="flex items-center gap-1">
                        Data de Compromisso
                        {sortConfig.key === 'committedDate' && (
                          <span className="text-blue-600">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th 
                      className={`${isMobile ? 'px-2 py-2' : 'px-4 py-3'} text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100`}
                      onClick={() => handleSort('closedDate')}
                    >
                      <div className="flex items-center gap-1">
                        Data de Fechamento
                        {sortConfig.key === 'closedDate' && (
                          <span className="text-blue-600">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th 
                      className={`${isMobile ? 'px-2 py-2' : 'px-4 py-3'} text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100`}
                      onClick={() => handleSort('leadTime')}
                    >
                      <div className="flex items-center gap-1">
                        Lead Time (dias)
                        {sortConfig.key === 'leadTime' && (
                          <span className="text-blue-600">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedTableData.map((item, idx) => (
                    <tr key={item['ID'] + idx} className="hover:bg-gray-50">
                      <td className={`${isMobile ? 'px-2 py-2' : 'px-4 py-3'} whitespace-nowrap text-sm font-medium text-gray-900`}>
                        {sanitizeText(item['ID']) || '-'}
                      </td>
                      <td className={`${isMobile ? 'px-2 py-2' : 'px-4 py-3'} whitespace-nowrap`}>
                        <span 
                          className="inline-flex px-2 py-1 text-xs font-semibold rounded-full text-white"
                          style={{ backgroundColor: getItemTypeColor(item['Tipo de Item'] || 'Item', allTypes) }}
                        >
                          {sanitizeText(item['Tipo de Item']) || 'Item'}
                        </span>
                      </td>
                      <td className={`${isMobile ? 'px-2 py-2' : 'px-4 py-3'} whitespace-nowrap text-sm text-gray-900`}>
                        {formatDateBR(item['Committed Date'])}
                      </td>
                      <td className={`${isMobile ? 'px-2 py-2' : 'px-4 py-3'} whitespace-nowrap text-sm text-gray-900`}>
                        {formatDateBR(item['Closed Date'])}
                      </td>
                      <td className={`${isMobile ? 'px-2 py-2' : 'px-4 py-3'} whitespace-nowrap text-sm font-semibold`}>
                        <span className={`${
                          item.leadTime <= 5 ? 'text-green-600' :
                          item.leadTime <= 15 ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {item.leadTime || 0}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {sortedTableData.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  Nenhum dado encontrado com os filtros aplicados
                </div>
              )}
            </div>
          )}
        </div>

        {/* 1. Tendência Temporal */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div 
            className="flex items-center justify-between cursor-pointer hover:bg-gray-100 -m-4 p-4 rounded-lg transition-colors"
            onClick={() => setIsTimelineExpanded(!isTimelineExpanded)}
          >
            <h3 className="text-lg font-semibold text-gray-800">
              Evolução das Estatísticas de Lead Time ao Longo do Tempo
              {!isTimelineExpanded && <span className="text-sm font-normal text-gray-500 ml-2">(clique para expandir)</span>}
            </h3>
            <button 
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              type="button"
            >
              <span>{isTimelineExpanded ? 'Colapsar' : 'Expandir'}</span>
              <span className={`transform transition-transform duration-300 ${isTimelineExpanded ? 'rotate-180' : ''}`}>
                ▼
              </span>
            </button>
          </div>
          
          {isTimelineExpanded && (
            <div className="mt-4 animate-in slide-in-from-top-2 duration-300">
              {/* Filtros das métricas */}
              <MetricsFilters 
                visibleMetrics={visibleMetrics}
                toggleMetric={toggleMetric}
              />
              
              {monthlyTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={monthlyTrend} margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="month" 
                  tickFormatter={formatMonth}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />
                <YAxis 
                  label={{ 
                    value: 'Lead Time (dias)', 
                    angle: -90, 
                    position: 'left', 
                    fontSize: isMobile ? 10 : 13, 
                    fill: '#374151', 
                    fontWeight: 600, 
                    dx: 0, 
                    dy: 0 
                  }}
                  tick={{ fontSize: isMobile ? 10 : 13, fill: '#374151', fontWeight: 500 }}
                />
                <Tooltip 
                  labelFormatter={(value) => `Período: ${formatMonth(value)}`}
                  formatter={(value, name) => {
                    const labels = {
                      avg: 'Média',
                      p50: 'P50',
                      p85: 'P85',
                      p95: 'P95'
                    };
                    return [`${value} dias`, labels[name] || name];
                  }}
                />
                {/* Linha da Média */}
                {visibleMetrics.avg && (
                  <Line 
                    type="monotone" 
                    dataKey="avg"
                    stroke="#3b82f6" 
                    strokeWidth={3}
                    name="Média"
                    dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                  />
                )}
                {/* Linha P50 */}
                {visibleMetrics.p50 && (
                  <Line 
                    type="monotone" 
                    dataKey="p50"
                    stroke="#10b981" 
                    strokeWidth={3}
                    name="P50"
                    dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                  />
                )}
                {/* Linha P85 */}
                {visibleMetrics.p85 && (
                  <Line 
                    type="monotone" 
                    dataKey="p85"
                    stroke="#f59e0b" 
                    strokeWidth={2}
                    name="P85"
                    dot={{ fill: '#f59e0b', strokeWidth: 2, r: 3 }}
                  />
                )}
                {/* Linha P95 */}
                {visibleMetrics.p95 && (
                  <Line 
                    type="monotone" 
                    dataKey="p95"
                    stroke="#ef4444" 
                    strokeWidth={2}
                    name="P95"
                    dot={{ fill: '#ef4444', strokeWidth: 2, r: 3 }}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-8 text-gray-500">
              Dados insuficientes para análise temporal
            </div>
          )}
            </div>
          )}
        </div>

        {/* 2. Distribuição por Faixas */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div 
            className="flex items-center justify-between cursor-pointer hover:bg-gray-100 -m-4 p-4 rounded-lg transition-colors"
            onClick={() => setIsDistributionExpanded(!isDistributionExpanded)}
          >
            <h3 className="text-lg font-semibold text-gray-800">
              Distribuição por Faixas de Lead Time
              {!isDistributionExpanded && <span className="text-sm font-normal text-gray-500 ml-2">(clique para expandir)</span>}
            </h3>
            <button 
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              type="button"
            >
              <span>{isDistributionExpanded ? 'Colapsar' : 'Expandir'}</span>
              <span className={`transform transition-transform duration-300 ${isDistributionExpanded ? 'rotate-180' : ''}`}>
                ▼
              </span>
            </button>
          </div>
          
          {isDistributionExpanded && (
            <div className="mt-4 animate-in slide-in-from-top-2 duration-300">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {distributionRanges.map((range, index) => (
                  <div 
                    key={range.range}
                    className="bg-white rounded-lg p-4 border-l-4 hover:shadow-md transition-shadow cursor-help relative group"
                    style={{ borderLeftColor: range.color }}
                  >
                    <div className="text-sm font-medium text-gray-600 mb-1">
                      {range.range}
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      {range.count}
                    </div>
                    <div className="text-sm text-gray-500">
                      {range.percentage}% do total
                    </div>
                    
                    {/* Tooltip */}
                    {range.items.length > 0 && (
                      <div className="absolute left-0 top-full mt-2 w-80 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 shadow-xl">
                        <div className="font-semibold mb-2 border-b border-gray-700 pb-2">
                          Itens nesta faixa ({range.items.length}):
                        </div>
                        <div className="max-h-48 overflow-y-auto space-y-1">
                          {range.items.slice(0, 20).map((item, idx) => (
                            <div key={idx} className="flex justify-between items-center py-1 border-b border-gray-700 last:border-b-0">
                              <div className="flex-1 truncate">
                                <span className="font-medium">{item.id}</span>
                                <span className="mx-2">-</span>
                                <span className="text-gray-300">{item.type}</span>
                              </div>
                              <div className="text-right ml-2">
                                <span className="font-medium">{item.leadTime}d</span>
                              </div>
                            </div>
                          ))}
                          {range.items.length > 20 && (
                            <div className="text-center text-gray-400 pt-2 border-t border-gray-700">
                              ... e mais {range.items.length - 20} itens
                            </div>
                          )}
                        </div>
                        {/* Seta do tooltip */}
                        <div className="absolute bottom-full left-8 w-0 h-0 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-gray-900"></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LeadTimeAnalysis;
